import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { Link } from '@/components/links';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconPay from '@/assets/icons/heroIcons/Pay.svg';
import IconRewards from '@/assets/icons/heroIcons/Rewards.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import IconLightBulb from '@/assets/icons/heroIcons/Light-bulb.svg';
import { AccountsAndRewards } from '@/sections/AccountsAndRewards';
import Hero from '@/sections/Hero';
import Eshops from '@/sections/Eshops';
import MoreInfoSection from '@/sections/MoreInfo';
import { FaqIntro } from '@/sections/FaqIntro';
import { Testimonials as TestimonialsSection } from '@/sections/Testimonials';
import { Button } from '@/components/buttons';
import { InfoBoxes } from '@/components/boxes';
import { BenefitTiles } from '@/components/tiles';
import { SectionHeadline } from '@/components/SectionHeadline';
import { Text } from '@/components/typography';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import { getPageBannerWithPromoSourceBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { SeoHeader } from '@/components/SeoHeader';
import { type EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import usePromoSource from '@/hooks/usePromoSource';
import { useClientOnly } from '@/hooks/useClientOnly';
import { getSectionAnchorId } from '@/utils/anchors';
import { showSpringOffer } from '@/utils/springOffer';
import IconTulip from '@/assets/icons/heroIcons/Tulip.svg';
import { useBnplLimitsMinimal } from '@/api/skippay/featureFlags';
interface Props extends IntlProps {
  pageBanners: PageBanner[] | null;
  eshops: EshopsList;
  testimonials: Testimonial[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(151);
  usePageView("deferred-payment");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["deferredPayment", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanners,
    eshops,
    menuPromoBanners,
    testimonials
  } = props;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = showSpringOffer();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shouldShowSpringOffer = t1;
  const isBnplLimitsMinimalEnabled = useBnplLimitsMinimal();
  let t2;
  if ($[2] !== t) {
    t2 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 5 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: cardIndex === 1 ? <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().whereToShop} analyticsParam="deferredPaymenthowItWorksPartners" testId="deferredPaymenthowItWorksPartners" /><Link target="_blank" to="https://promo.skippay.cz/bank-id/" analyticsParam="deferredPaymenthowItWorksBankID" testId="deferredPaymenthowItWorksBankID" /></Trans> : t(`howItWorks.items.${cardIndex}.text`)
        } : {
          footer: <Button analyticsParam="deferredPaymenthowItWorksiWantMaxi" testId="deferredPaymenthowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={LINKS().registration}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/deferredPayment/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: cardIndex === 1 ? `/images/sections/deferredPayment/cards/cs/mobile/${cardIndex}.png` : `/images/sections/deferredPayment/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const cards = t2;
  const promoSource = usePromoSource();
  const {
    isClient
  } = useClientOnly();
  let t3;
  if ($[4] !== isClient || $[5] !== pageBanners || $[6] !== promoSource) {
    t3 = isClient && promoSource && pageBanners?.find(banner_0 => banner_0.promoSource === promoSource) || pageBanners?.find(_temp);
    $[4] = isClient;
    $[5] = pageBanners;
    $[6] = promoSource;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const pageBanner = t3;
  let t4;
  if ($[8] !== t) {
    t4 = t("seo.title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("seo.description");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <SeoHeader title={t4} description={t5} />;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = pageBanner ? pageBanner.videoId : "BCyXQ3Kx4zY";
  let t8;
  if ($[15] !== pageBanner?.title || $[16] !== t) {
    t8 = pageBanner?.title ?? t("banner.title");
    $[15] = pageBanner?.title;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== pageBanner?.text || $[19] !== t) {
    t9 = pageBanner?.text ?? t("banner.text");
    $[18] = pageBanner?.text;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== pageBanner || $[22] !== t) {
    t10 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().registration,
      testId: "deferredPaymentHeroPrimaryCTA",
      analyticsParam: "deferredPaymentHeroPrimaryCTA",
      variant: "primary"
    }];
    $[21] = pageBanner;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = pageBanner?.image?.url ?? "/images/sections/deferredPayment/hero/hero.png";
  let t12;
  if ($[24] !== pageBanner?.image?.alt || $[25] !== t) {
    t12 = pageBanner?.image?.alt ?? t("banner.title");
    $[24] = pageBanner?.image?.alt;
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = {
      url: t11,
      alt: t12
    };
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = pageBanner?.variant || "products";
  let t15;
  if ($[30] !== t10 || $[31] !== t13 || $[32] !== t14 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t15 = <Hero videoId={t7} title={t8} text={t9} buttons={t10} image={t13} variant={t14} />;
    $[30] = t10;
    $[31] = t13;
    $[32] = t14;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = getSectionAnchorId("advantages");
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("advantages.title");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <SectionHeadline className="mb-10 lg:mb-16" variant="inverted" anchorKey="advantages.title">{t17}</SectionHeadline>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("advantages.items.1.title");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t) {
    t20 = t("advantages.items.1.text");
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IconDeferredPayment />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t19 || $[48] !== t20) {
    t22 = {
      title: t19,
      paragraph: t20,
      icon: t21
    };
    $[47] = t19;
    $[48] = t20;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t) {
    t23 = t("advantages.items.2.title");
    $[50] = t;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("advantages.items.2.text");
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <IconPay />;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t23 || $[56] !== t24) {
    t26 = {
      title: t23,
      paragraph: t24,
      icon: t25
    };
    $[55] = t23;
    $[56] = t24;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t) {
    t27 = shouldShowSpringOffer ? t("advantages.items.spring.title") : t("advantages.items.3.title");
    $[58] = t;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = shouldShowSpringOffer ? t("advantages.items.spring.text") : t("advantages.items.3.text");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = shouldShowSpringOffer ? <IconTulip /> : <IconRewards />;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t27 || $[64] !== t28) {
    t30 = {
      title: t27,
      paragraph: t28,
      icon: t29
    };
    $[63] = t27;
    $[64] = t28;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t22 || $[67] !== t26 || $[68] !== t30) {
    t31 = <InfoBoxes items={[t22, t26, t30]} variant="inverted" withCharacters={true} />;
    $[66] = t22;
    $[67] = t26;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = LINKS();
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  const t33 = t32.registration;
  let t34;
  if ($[71] !== t) {
    t34 = t("advantages.cta");
    $[71] = t;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t34) {
    t35 = <Button to={t33} testId="deferredPaymentRegistration" analyticsParam="deferredPaymentRegistration" size="lg" variant="primary" align="center" className="mt-12 md:mt-16">{t34}</Button>;
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== t18 || $[76] !== t31 || $[77] !== t35) {
    t36 = <AnimatedContent className="px-4 pb-10 lg:pb-20">{t18}{t31}{t35}</AnimatedContent>;
    $[75] = t18;
    $[76] = t31;
    $[77] = t35;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t) {
    t37 = t("howItWorks.title");
    $[79] = t;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t37) {
    t38 = <SectionHeadline className="mb-10 px-4 md:mb-16" variant="inverted" anchorKey="howItWorks.title">{t37}</SectionHeadline>;
    $[81] = t37;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== cards) {
    t39 = <StackCards imagePosition="left" cards={cards} />;
    $[83] = cards;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== t38 || $[86] !== t39) {
    t40 = <AnimatedContent className="pt-10 pb-14 lg:pt-20 lg:pb-32">{t38}{t39}</AnimatedContent>;
    $[85] = t38;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== t36 || $[89] !== t40) {
    t41 = <AnimatedSection bgColor="bg-blue-900" id={t16}>{t36}{t40}</AnimatedSection>;
    $[88] = t36;
    $[89] = t40;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = getSectionAnchorId("deferredPayment");
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== eshops) {
    t43 = <AnimatedContent className="pb-10 lg:pb-20"><Eshops eshops={eshops} /></AnimatedContent>;
    $[92] = eshops;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <AnimatedContent className="py-10 lg:py-20"><AccountsAndRewards /></AnimatedContent>;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== isBnplLimitsMinimalEnabled || $[96] !== t) {
    t45 = !isBnplLimitsMinimalEnabled && <AnimatedContent className="px-4 pt-10 lg:pt-20"><div className="mx-auto w-full max-w-[50rem] text-center"><SectionHeadline className="mb-5" anchorKey="deferredPayment.title">{t("deferredPayment.title")}</SectionHeadline><Text variant="p2" className="text-gray-800"><Trans t={t} i18nKey="deferredPayment.text"><Link to={LINKS().revolving} testId="deferredPaymentAboutRevolving" analyticsParam="deferredPaymentAboutRevolving" /></Trans></Text></div><div className="my-8 mb-12 md:my-16"><BenefitTiles items={[{
          title: t("deferredPayment.items.1.title")
        }, shouldShowSpringOffer ? {
          title: t("deferredPayment.items.spring.title")
        } : {
          title: t("deferredPayment.items.2.title")
        }, {
          title: t("deferredPayment.items.3.title")
        }].filter(Boolean)} /></div><Button to={LINKS().revolving} testId="deferredPaymentRevolving" analyticsParam="deferredPaymentRevolving" size="lg" variant="primary" align="center">{t("deferredPayment.cta")}</Button></AnimatedContent>;
    $[95] = isBnplLimitsMinimalEnabled;
    $[96] = t;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t43 || $[99] !== t45) {
    t46 = <AnimatedSection bgColor="bg-blue-50" id={t42}>{t43}{t44}{t45}</AnimatedSection>;
    $[98] = t43;
    $[99] = t45;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = getSectionAnchorId("moreInfo");
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] !== testimonials) {
    t48 = <AnimatedContent className="relative z-10 pb-10 lg:pb-20"><TestimonialsSection testimonials={testimonials} /></AnimatedContent>;
    $[102] = testimonials;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = LINKS();
    $[104] = t49;
  } else {
    t49 = $[104];
  }
  let t50;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Link to="/caste-dotazy/odlozena-platba" analyticsParam="faqDeferredPayment" testId="faqDeferredPayment" />;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] !== t) {
    t51 = <AnimatedContent className="relative z-10 py-10 lg:py-20"><FaqIntro link={t49.faq} text={<Trans i18nKey="faq.text" t={t}>{t50}</Trans>} /></AnimatedContent>;
    $[106] = t;
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <IconThird className="fillButtonIcon" />;
    $[108] = t52;
  } else {
    t52 = $[108];
  }
  let t53;
  if ($[109] !== t) {
    t53 = t("core:products.payInThree");
    $[109] = t;
    $[110] = t53;
  } else {
    t53 = $[110];
  }
  let t54;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = LINKS();
    $[111] = t54;
  } else {
    t54 = $[111];
  }
  let t55;
  if ($[112] !== t53) {
    t55 = {
      analyticsParam: "deferredPaymentMoreInfoPayInThree",
      icon: t52,
      testId: "deferredPaymentMoreInfoPayInThree",
      title: t53,
      to: t54.payInThree
    };
    $[112] = t53;
    $[113] = t55;
  } else {
    t55 = $[113];
  }
  let t56;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <IconMaxi className="fillButtonIcon" />;
    $[114] = t56;
  } else {
    t56 = $[114];
  }
  let t57;
  if ($[115] !== t) {
    t57 = t("core:products.revolving");
    $[115] = t;
    $[116] = t57;
  } else {
    t57 = $[116];
  }
  let t58;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = LINKS();
    $[117] = t58;
  } else {
    t58 = $[117];
  }
  let t59;
  if ($[118] !== t57) {
    t59 = {
      analyticsParam: "deferredPaymentMoreInfoRevolvingApplication",
      icon: t56,
      testId: "deferredPaymentMoreInfoRevolvingApplication",
      title: t57,
      to: t58.revolving
    };
    $[118] = t57;
    $[119] = t59;
  } else {
    t59 = $[119];
  }
  let t60;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <IconCard className="fillButtonIcon" />;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== t) {
    t61 = t("core:products.card");
    $[121] = t;
    $[122] = t61;
  } else {
    t61 = $[122];
  }
  let t62;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = LINKS();
    $[123] = t62;
  } else {
    t62 = $[123];
  }
  let t63;
  if ($[124] !== t61) {
    t63 = {
      analyticsParam: "deferredPaymentMoreInfoCard",
      icon: t60,
      testId: "deferredPaymentMoreInfoCard",
      title: t61,
      to: t62.card
    };
    $[124] = t61;
    $[125] = t63;
  } else {
    t63 = $[125];
  }
  let t64;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <IconLightBulb className="fillButtonIcon" />;
    $[126] = t64;
  } else {
    t64 = $[126];
  }
  let t65;
  if ($[127] !== t) {
    t65 = t("core:link.shopWisely");
    $[127] = t;
    $[128] = t65;
  } else {
    t65 = $[128];
  }
  let t66;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = LINKS();
    $[129] = t66;
  } else {
    t66 = $[129];
  }
  let t67;
  if ($[130] !== t65) {
    t67 = {
      analyticsParam: "deferredPaymentMoreInfoShopWisely",
      icon: t64,
      testId: "deferredPaymentMoreInfoShopWisely",
      title: t65,
      to: t66.shopWisely
    };
    $[130] = t65;
    $[131] = t67;
  } else {
    t67 = $[131];
  }
  let t68;
  if ($[132] !== t55 || $[133] !== t59 || $[134] !== t63 || $[135] !== t67) {
    t68 = <AnimatedContent className="relative z-10"><MoreInfoSection buttons={[t55, t59, t63, t67]} /></AnimatedContent>;
    $[132] = t55;
    $[133] = t59;
    $[134] = t63;
    $[135] = t67;
    $[136] = t68;
  } else {
    t68 = $[136];
  }
  let t69;
  let t70;
  let t71;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <div className="bgCircle absolute top-1/4 right-0 translate-x-1/3 rotate-270 md:hidden" />;
    t70 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    t71 = <div className="bgCircle absolute top-0 left-0 -translate-x-1/2 translate-y-1/4 rotate-180" />;
    $[137] = t69;
    $[138] = t70;
    $[139] = t71;
  } else {
    t69 = $[137];
    t70 = $[138];
    t71 = $[139];
  }
  let t72;
  if ($[140] !== t48 || $[141] !== t51 || $[142] !== t68) {
    t72 = <AnimatedSection bgColor="bg-blue-100" className="pb-0!" id={t47}><div className="relative overflow-hidden">{t48}{t51}{t68}{t69}{t70}{t71}</div></AnimatedSection>;
    $[140] = t48;
    $[141] = t51;
    $[142] = t68;
    $[143] = t72;
  } else {
    t72 = $[143];
  }
  let t73;
  if ($[144] !== menuPromoBanners || $[145] !== t15 || $[146] !== t41 || $[147] !== t46 || $[148] !== t6 || $[149] !== t72) {
    t73 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t15}{t41}{t46}{t72}</Layout>;
    $[144] = menuPromoBanners;
    $[145] = t15;
    $[146] = t41;
    $[147] = t46;
    $[148] = t6;
    $[149] = t72;
    $[150] = t73;
  } else {
    t73 = $[150];
  }
  return t73;
}
function _temp(banner) {
  return !banner.promoSource;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('platba-skip-pay');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanners: await getPageBannerWithPromoSourceBySlug('deferred-payment'),
      testimonials: await getTestimonialsBySlug('deferred-payment'),
      eshops: await getAffiliateEshopsList('deferred-payment'),
      ...(await getIntlProps(ctx, ['core', 'eshops', 'deferredPayment', 'homepage', 'testimonials']))
    }
  };
}) satisfies GetStaticProps<Props>;